import { useRef } from "react";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { type IProductImage } from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import PlayVideoIcon from "@/app/components/common-builder/gallery/PlayVideoIcon";
import MediaPreviewModal from "@/app/components/lib/media-preview-provider/MediaPreviewModal";
import NextImage from "@/app/components/lib/NextImage";
import SvgBarLove from "@/app/layouts/mobile-layout/mobile-navigator/navigator-icons/SvgBarLove";
import SvgFilledLove from "@/app/layouts/mobile-layout/mobile-navigator/navigator-icons/SvgFilledLove";
import { useElementWidth } from "@/hooks/useElementWidth";
import useIsRtl from "@/hooks/useIsRtl";
import PreviewProvider from "./PreviewProvider";
import ThumbMailSlider from "./slider-with-thumbmails/ThumbMailSlider";

interface Props {
  productImages: IProductImage[];
  id: number;
  isPC: boolean;
  isSmall?: boolean;
  productName: string;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

const SliderWithThumbs: React.FC<Props> = ({
  productImages,
  isPC,
  id,
  isSmall = false,
  productName,
  isFavorite = false,
  onToggleFavorite,
}) => {
  const isRTL = useIsRtl();
  const getCompany = useGetCompanyQuery();
  const ratio =
    getCompany?.data?.result?.theme?.components?.product?.image_ratio ?? 1;
  const autoPlay =
    !!getCompany?.data?.result?.theme?.components?.product?.auto_image_scroller;
  const containerRef = useRef<HTMLDivElement>(null);
  const size = isSmall ? 48 : 137;
  // (size + 12) because of spasing in ThumbMailSlider!!
  const thumbMails = isPC ? productImages?.map((im) => ({ ...im })) : [];
  const thumbnailsWidth =
    thumbMails?.length > 1 ? size - (isSmall ? 8 : 16) : 0;
  const containerWidth = useElementWidth(containerRef);
  const sliderWidth = Math.max(containerWidth - 0.1 - thumbnailsWidth, 0);

  const videoPreviewRef = useRef<{
    showMedia(src: string, type: "video" | "image"): void;
  } | null>(null);

  const imagesUrl = productImages
    .filter((m) => m.type === "image")
    .map((item) => item.image_url);

  return (
    <div ref={containerRef} className="w-100" style={{ position: "relative" }}>
      <PreviewProvider images={imagesUrl}>
        {(openGallery) => (
          <ThumbMailSlider
            thumbnails={thumbMails}
            id={id}
            size={size}
            isSamll={isSmall}
            sliderWidth={sliderWidth}
            ratio={ratio}
            autoPlay={autoPlay}
          >
            {productImages?.map((img, key) => (
              <div
                key={key}
                // className="unset-img"
                style={{
                  borderRadius: 16,
                  display: "block",
                  margin: "auto",
                  textAlign: "center",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {img?.type === "video" && (
                  <PlayVideoIcon
                    style={{
                      position: "absolute",
                      top: "45%",
                      left: "45%",
                      zIndex: 5,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      videoPreviewRef.current?.showMedia(
                        img.image_url,
                        img?.type ?? "image",
                      );
                    }}
                  />
                )}
                <NextImage
                  priority={key === 0}
                  alt={`${productName} image ${key}`}
                  src={
                    img?.type === "image" ? img.image_url : img.thumbnail_url
                  }
                  width={sliderWidth}
                  height={sliderWidth * ratio}
                  // layout="fill"
                  // className="custom-img"
                  onClick={() => {
                    if (img?.type === "image") {
                      const imageIndex = imagesUrl.findIndex(
                        (mUrl) => mUrl === img.image_url,
                      );
                      return openGallery(imageIndex);
                    }
                    videoPreviewRef.current?.showMedia(
                      img.image_url,
                      img?.type ?? "image",
                    );
                  }}
                  objectFit="contain"
                />
              </div>
            ))}
          </ThumbMailSlider>
        )}
      </PreviewProvider>
      <div
        style={{
          position: "absolute",
          top: isSmall ? 8 : 32,
          right: isRTL ? undefined : isSmall ? 8 : 16,
          left: isRTL ? (isSmall ? 8 : 16) : undefined,
          display: "flex",
          gap: 8,
          zIndex: 10,
        }}
      >
        <div
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavorite?.();
          }}
          className={`product-favorite-button ${
            isFavorite ? "selected" : "unselected"
          }`}
        >
          {isFavorite ? (
            <SvgFilledLove size={24} />
          ) : (
            <SvgBarLove selected={isFavorite} size={24} />
          )}
        </div>
      </div>
      <MediaPreviewModal ref={videoPreviewRef} />
    </div>
  );
};

export default SliderWithThumbs;
